/**
 * NpcSystem.js
 *
 * Phase 12b — NPC Life for the KRAKEN.
 *
 * Three sub-systems wrapped in one class:
 *
 *  1. PEDESTRIANS — small wandering NPCs around the city plaza. They pick
 *     random waypoints inside the plaza radius, walk between them, and idle
 *     occasionally. Cosmetic only; no interaction.
 *
 *  2. WILDLIFE — small non-hostile critters on habitable Terrestrial planets.
 *     They wander within a radius around the player and despawn/respawn as
 *     the player moves, so we never see them appear inside an actively
 *     traveled zone but the world always feels populated.
 *
 *  3. AMBIENT SHIPS — decorative NPC ships flying lazy paths through the
 *     system-space scene. Each picks two random points in the inner orbital
 *     band and shuttles between them.
 *
 * All three are seeded from the body / system so visiting the same city
 * twice yields the same NPC names & spawn positions (when a name list is
 * provided).
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
} from '@babylonjs/core';

// --- Tunables ---
const PED_COUNT_MIN = 5;
const PED_COUNT_MAX = 9;
const PED_SPEED = 1.4;          // m/s walk pace
const PED_IDLE_CHANCE = 0.25;
const PED_DESPAWN_DIST = 90;    // hide when player wanders far from plaza
const PED_INTERACT_RADIUS = 3.5;

// Phase 13 (P3) — NPC personalities: a small persistent name + bark pool.
const PED_FIRST_NAMES = [
  'Mira', 'Jax', 'Vela', 'Orin', 'Nyx', 'Talos', 'Iyla', 'Reva', 'Kael', 'Sora',
  'Bryn', 'Ezra', 'Lin', 'Cass', 'Dax', 'Tova', 'Aro', 'Niv', 'Yara', 'Pell',
];
const PED_LAST_NAMES = [
  'Crane', 'Vol', 'Rhys', 'Quill', 'Ash', 'Soren', 'Dare', 'Marsh',
  'Brokk', 'Vex', 'Mire', 'Doss', 'Fane', 'Glen', 'Hask', 'Ivar',
];
const PED_BARKS = [
  "The smelters were running hot all night — feels like the planet's listening.",
  "Saw something move on the ridge again. Probably nothing.",
  "If you're heading off-world, mind the asteroids in lane two.",
  "We don't get many visitors. Welcome to the colony.",
  "The captain has a bounty out — purple-marked, fast, mean. Be careful.",
  "Don't trust anyone selling cheap nanites. Trust me.",
  "I miss real sky. This dome will have to do.",
  "Heard the survey crew found something out by the third anomaly. Worth a look.",
  "Stay sharp, traveler. The wastes have eyes.",
  "The refinery pays a premium today — bring me ore.",
];

const WILD_COUNT = 5;
const WILD_SPEED = 1.8;
const WILD_RADIUS = 40;         // wander radius around player
const WILD_DESPAWN_DIST = 100;

const SHIP_COUNT = 4;
const SHIP_SPEED_MIN = 80;
const SHIP_SPEED_MAX = 220;

// Tiny LCG random for deterministic seeding
function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0xdeadbeef;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export class NpcSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this._disposed = false;

    this._pedRoot = new TransformNode('npcPedRoot', scene);
    this._wildRoot = new TransformNode('npcWildRoot', scene);
    this._shipRoot = new TransformNode('npcShipRoot', scene);
    this._shipRoot.setEnabled(false);

    this._peds = [];
    this._pedConfig = null; // { center: Vector3, radius: number }

    this._wild = [];
    this._wildEnabled = false;

    this._ships = [];
  }

  // ============================================================
  // PEDESTRIANS
  // ============================================================

  /** Spawn pedestrians around a city plaza. Call after CitySystem.setCity. */
  setupPedestrians(plazaWorld, plazaRadius, seed = 1337) {
    this.clearPedestrians();
    if (!plazaWorld) return;
    const rng = mkRng(seed ^ 0x9e3779b9);
    const count = Math.floor(PED_COUNT_MIN + rng() * (PED_COUNT_MAX - PED_COUNT_MIN));
    const radius = Math.max(4, plazaRadius || 12);
    this._pedConfig = { center: plazaWorld.clone(), radius };

    for (let i = 0; i < count; i++) {
      const npc = this._buildPedMesh(i, rng);
      // Place at a random point inside the plaza
      const ang = rng() * Math.PI * 2;
      const r = rng() * radius * 0.85;
      const x = plazaWorld.x + Math.cos(ang) * r;
      const z = plazaWorld.z + Math.sin(ang) * r;
      const y = this.getTerrainHeight(x, z);
      npc.root.position.set(x, y, z);
      npc.target = this._pickPedTarget(plazaWorld, radius, rng);
      npc.idleTimer = rng() * 1.5;
      npc.bobPhase = rng() * Math.PI * 2;
      npc.speed = PED_SPEED * (0.85 + rng() * 0.4);
      // Phase 13 — persistent name + bark
      const first = PED_FIRST_NAMES[Math.floor(rng() * PED_FIRST_NAMES.length)];
      const last = PED_LAST_NAMES[Math.floor(rng() * PED_LAST_NAMES.length)];
      npc.name = `${first} ${last}`;
      npc.bark = PED_BARKS[Math.floor(rng() * PED_BARKS.length)];
      this._peds.push(npc);
    }
  }

  _buildPedMesh(idx, rng) {
    const root = new TransformNode(`ped_${idx}_root`, this.scene);
    root.parent = this._pedRoot;

    // Color: vary suit color slightly
    const hue = rng();
    const color = new Color3(
      0.25 + hue * 0.5,
      0.30 + (1 - hue) * 0.4,
      0.55 + rng() * 0.35,
    );
    const mat = new StandardMaterial(`pedMat_${idx}`, this.scene);
    mat.diffuseColor = color;
    mat.emissiveColor = color.scale(0.12);
    mat.specularColor = new Color3(0.1, 0.1, 0.15);

    const headMat = new StandardMaterial(`pedHeadMat_${idx}`, this.scene);
    headMat.diffuseColor = new Color3(0.65, 0.55, 0.5);
    headMat.specularColor = new Color3(0, 0, 0);

    // Body (capsule-ish: stretched sphere)
    const body = MeshBuilder.CreateCapsule(`ped_${idx}_body`, {
      radius: 0.28,
      height: 1.1,
      tessellation: 8,
      subdivisions: 1,
    }, this.scene);
    body.position.y = 0.55;
    body.material = mat;
    body.parent = root;
    body.isPickable = false;

    // Head
    const head = MeshBuilder.CreateSphere(`ped_${idx}_head`, {
      diameter: 0.32, segments: 8,
    }, this.scene);
    head.position.y = 1.32;
    head.material = headMat;
    head.parent = root;
    head.isPickable = false;

    // Backpack / detail block — adds silhouette variety
    const pack = MeshBuilder.CreateBox(`ped_${idx}_pack`, {
      width: 0.32, height: 0.4, depth: 0.18,
    }, this.scene);
    pack.position.set(0, 0.7, -0.22);
    pack.material = mat;
    pack.parent = root;
    pack.isPickable = false;

    return { root, body, head, pack, mat, target: null, idleTimer: 0, bobPhase: 0, speed: PED_SPEED };
  }

  _pickPedTarget(center, radius, rng) {
    const ang = rng() * Math.PI * 2;
    const r = (0.2 + rng() * 0.75) * radius;
    return new Vector3(center.x + Math.cos(ang) * r, 0, center.z + Math.sin(ang) * r);
  }

  clearPedestrians() {
    for (const p of this._peds) {
      try { p.root.dispose(); } catch (_e) { /* noop */ }
    }
    this._peds = [];
    this._pedConfig = null;
  }

  /** Phase 13 — returns the nearest pedestrian within interact range, or null. */
  getNearPedestrian() {
    if (this._peds.length === 0) return null;
    const p = this.getPlayerPos();
    let best = null;
    let bestDist = PED_INTERACT_RADIUS;
    for (const np of this._peds) {
      if (!np.root.isEnabled()) continue;
      const d = Math.hypot(np.root.position.x - p.x, np.root.position.z - p.z);
      if (d < bestDist) {
        bestDist = d;
        best = { name: np.name, bark: np.bark, distance: d };
      }
    }
    return best;
  }

  _tickPedestrians(dt, rng) {
    if (!this._pedConfig || this._peds.length === 0) return;
    const playerPos = this.getPlayerPos();
    const farFromPlaza = playerPos
      ? Vector3.Distance(playerPos, this._pedConfig.center) > PED_DESPAWN_DIST
      : false;
    for (const p of this._peds) {
      // Cull rendering when player is far from the plaza
      const visible = !farFromPlaza;
      if (p.root.isEnabled() !== visible) p.root.setEnabled(visible);
      if (!visible) continue;

      // Idle?
      if (p.idleTimer > 0) {
        p.idleTimer -= dt;
        continue;
      }
      if (!p.target) {
        p.target = this._pickPedTarget(this._pedConfig.center, this._pedConfig.radius, rng);
      }
      const cur = p.root.position;
      const dx = p.target.x - cur.x;
      const dz = p.target.z - cur.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.4) {
        // Pick new target or idle briefly
        if (rng() < PED_IDLE_CHANCE) {
          p.idleTimer = 1.5 + rng() * 3;
        }
        p.target = this._pickPedTarget(this._pedConfig.center, this._pedConfig.radius, rng);
        continue;
      }
      const stepX = (dx / dist) * p.speed * dt;
      const stepZ = (dz / dist) * p.speed * dt;
      cur.x += stepX;
      cur.z += stepZ;
      cur.y = this.getTerrainHeight(cur.x, cur.z);
      // Face direction of travel
      p.root.rotation.y = Math.atan2(stepX, stepZ);
      // Walk bob
      p.bobPhase += dt * 8;
      p.body.position.y = 0.55 + Math.sin(p.bobPhase) * 0.04;
      p.head.position.y = 1.32 + Math.sin(p.bobPhase) * 0.04;
    }
  }

  // ============================================================
  // WILDLIFE
  // ============================================================

  /** Enable / disable wildlife for the current body. Pass null to clear. */
  setWildlife(body) {
    this.clearWildlife();
    if (!body) return;
    if (body.kind === 'moon') return;
    if (body.type !== 'Terrestrial') return;
    if ((body.habitability ?? 0) < 35) return;
    this._wildEnabled = true;
    const rng = mkRng((body.radius_km | 0) ^ 0x5eedb1fe);
    for (let i = 0; i < WILD_COUNT; i++) {
      const w = this._buildWildlifeMesh(i, rng);
      this._wild.push(w);
    }
  }

  _buildWildlifeMesh(idx, rng) {
    const root = new TransformNode(`wild_${idx}_root`, this.scene);
    root.parent = this._wildRoot;

    const mat = new StandardMaterial(`wildMat_${idx}`, this.scene);
    // Earthy critter color
    mat.diffuseColor = new Color3(
      0.35 + rng() * 0.3,
      0.25 + rng() * 0.25,
      0.15 + rng() * 0.15,
    );
    mat.specularColor = new Color3(0, 0, 0);

    const body = MeshBuilder.CreateSphere(`wild_${idx}_body`, {
      diameterX: 0.6, diameterY: 0.45, diameterZ: 0.85, segments: 8,
    }, this.scene);
    body.position.y = 0.32;
    body.material = mat;
    body.parent = root;
    body.isPickable = false;

    // Tail
    const tail = MeshBuilder.CreateBox(`wild_${idx}_tail`, {
      width: 0.08, height: 0.08, depth: 0.35,
    }, this.scene);
    tail.position.set(0, 0.35, -0.5);
    tail.material = mat;
    tail.parent = root;
    tail.isPickable = false;

    // Tiny ears / dots for a friendlier silhouette
    const ear = MeshBuilder.CreateSphere(`wild_${idx}_ear`, { diameter: 0.14, segments: 6 }, this.scene);
    ear.position.set(0, 0.55, 0.35);
    ear.material = mat;
    ear.parent = root;
    ear.isPickable = false;

    // Initial position will be set on first tick when we know player position
    root.setEnabled(false);
    return {
      root, body, mat,
      target: null,
      bobPhase: rng() * Math.PI * 2,
      speed: WILD_SPEED * (0.7 + rng() * 0.6),
      spawned: false,
    };
  }

  clearWildlife() {
    for (const w of this._wild) {
      try { w.root.dispose(); } catch (_e) { /* noop */ }
    }
    this._wild = [];
    this._wildEnabled = false;
  }

  _spawnWildAround(w, playerPos, rng) {
    const ang = rng() * Math.PI * 2;
    const r = 12 + rng() * (WILD_RADIUS - 12);
    const x = playerPos.x + Math.cos(ang) * r;
    const z = playerPos.z + Math.sin(ang) * r;
    const y = this.getTerrainHeight(x, z);
    w.root.position.set(x, y, z);
    w.target = new Vector3(x + (rng() - 0.5) * 6, 0, z + (rng() - 0.5) * 6);
    w.spawned = true;
    w.root.setEnabled(true);
  }

  _tickWildlife(dt, rng) {
    if (!this._wildEnabled || this._wild.length === 0) return;
    const playerPos = this.getPlayerPos();
    if (!playerPos) return;
    for (const w of this._wild) {
      if (!w.spawned) {
        this._spawnWildAround(w, playerPos, rng);
        continue;
      }
      const distFromPlayer = Vector3.Distance(w.root.position, playerPos);
      if (distFromPlayer > WILD_DESPAWN_DIST) {
        // Respawn somewhere closer to the player
        this._spawnWildAround(w, playerPos, rng);
        continue;
      }
      // Flee from the player when they get close (passive prey behaviour).
      if (distFromPlayer < WILD_FLEE_RADIUS) {
        const ax = w.root.position.x - playerPos.x;
        const az = w.root.position.z - playerPos.z;
        const al = Math.hypot(ax, az) || 1;
        w.target = new Vector3(
          w.root.position.x + (ax / al) * 16,
          0,
          w.root.position.z + (az / al) * 16,
        );
        w._fleeing = true;
      } else if (w._fleeing && distFromPlayer > WILD_FLEE_RADIUS * 1.9) {
        w._fleeing = false;
      }
      // Wander toward target
      if (!w.target) {
        const ang = rng() * Math.PI * 2;
        const r = 3 + rng() * 8;
        w.target = new Vector3(
          w.root.position.x + Math.cos(ang) * r,
          0,
          w.root.position.z + Math.sin(ang) * r,
        );
      }
      const cur = w.root.position;
      const dx = w.target.x - cur.x;
      const dz = w.target.z - cur.z;
      const d = Math.hypot(dx, dz);
      if (d < 0.3) {
        w.target = null;
        continue;
      }
      const stepX = (dx / d) * w.speed * dt;
      const stepZ = (dz / d) * w.speed * dt;
      cur.x += stepX;
      cur.z += stepZ;
      cur.y = this.getTerrainHeight(cur.x, cur.z);
      w.root.rotation.y = Math.atan2(stepX, stepZ);
      // Hop bob
      w.bobPhase += dt * 10;
      w.body.position.y = 0.32 + Math.abs(Math.sin(w.bobPhase)) * 0.12;
    }
  }

  // ============================================================
  // AMBIENT SHIPS (space mode)
  // ============================================================

  /** Spawn ambient NPC ships parented to the supplied space root.
   *  Call once when the space scene is built. Pass `null` to clear. */
  setupAmbientShips(spaceRoot, seed = 9001, lanesRadius = 4000) {
    this.clearAmbientShips();
    if (!spaceRoot) return;
    const rng = mkRng(seed ^ 0xa11e0a9);
    for (let i = 0; i < SHIP_COUNT; i++) {
      const ship = this._buildShipMesh(i, rng);
      ship.root.parent = this._shipRoot;
      this._placeShipOnNewLane(ship, rng, lanesRadius);
      this._ships.push(ship);
    }
    // We parent the entire shipRoot to the space root so toggling space-mode
    // visibility takes our ships along for the ride.
    this._shipRoot.parent = spaceRoot;
    this._shipRoot.setEnabled(true);
  }

  _buildShipMesh(idx, rng) {
    const root = new TransformNode(`npcShip_${idx}_root`, this.scene);

    const hueRoll = rng();
    const hullColor = new Color3(
      0.55 + hueRoll * 0.3,
      0.55 + (1 - hueRoll) * 0.25,
      0.65 + rng() * 0.25,
    );

    const hullMat = new StandardMaterial(`npcShipHull_${idx}`, this.scene);
    hullMat.diffuseColor = hullColor;
    hullMat.emissiveColor = hullColor.scale(0.18);
    hullMat.specularColor = new Color3(0.6, 0.6, 0.7);

    const glowMat = new StandardMaterial(`npcShipGlow_${idx}`, this.scene);
    glowMat.diffuseColor = new Color3(0.1, 0.2, 0.3);
    glowMat.emissiveColor = new Color3(0.3, 0.6, 1.0);
    glowMat.specularColor = new Color3(0, 0, 0);

    // Simple delta-wing: stretched ellipsoid + a thin wing box + glow at tail
    const hull = MeshBuilder.CreateSphere(`npcShip_${idx}_hull`, {
      diameterX: 22, diameterY: 8, diameterZ: 44, segments: 14,
    }, this.scene);
    hull.material = hullMat;
    hull.parent = root;
    hull.isPickable = false;

    const wing = MeshBuilder.CreateBox(`npcShip_${idx}_wing`, {
      width: 60, height: 1.4, depth: 14,
    }, this.scene);
    wing.position.set(0, 0, -2);
    wing.material = hullMat;
    wing.parent = root;
    wing.isPickable = false;

    const tail = MeshBuilder.CreateSphere(`npcShip_${idx}_tail`, {
      diameter: 4.5, segments: 10,
    }, this.scene);
    tail.position.set(0, 0, -23);
    tail.material = glowMat;
    tail.parent = root;
    tail.isPickable = false;

    // Soft point light on the tail so the ship reads in vacuum
    const light = new PointLight(`npcShip_${idx}_lt`, new Vector3(0, 0, -22), this.scene);
    light.diffuse = new Color3(0.3, 0.6, 1.0);
    light.intensity = 1.8;
    light.range = 220;
    light.parent = root;

    return {
      root, hull, wing, tail, light,
      from: new Vector3(0, 0, 0),
      to: new Vector3(0, 0, 0),
      progress: 0,
      duration: 1,
    };
  }

  _placeShipOnNewLane(ship, rng, lanesRadius) {
    const r1 = (0.35 + rng() * 0.55) * lanesRadius;
    const r2 = (0.35 + rng() * 0.55) * lanesRadius;
    const a1 = rng() * Math.PI * 2;
    const a2 = a1 + (rng() - 0.5) * 1.6 + Math.PI; // roughly opposite arc
    const y1 = (rng() - 0.5) * 240;
    const y2 = (rng() - 0.5) * 240;
    ship.from = new Vector3(Math.cos(a1) * r1, y1, Math.sin(a1) * r1);
    ship.to = new Vector3(Math.cos(a2) * r2, y2, Math.sin(a2) * r2);
    const dist = Vector3.Distance(ship.from, ship.to);
    const speed = SHIP_SPEED_MIN + rng() * (SHIP_SPEED_MAX - SHIP_SPEED_MIN);
    ship.duration = Math.max(20, dist / speed);
    ship.progress = 0;
    ship.root.position.copyFrom(ship.from);
    // Aim ship at destination
    const dir = ship.to.subtract(ship.from);
    ship.root.rotation.y = Math.atan2(dir.x, dir.z);
    ship.root.rotation.x = -Math.atan2(dir.y, Math.hypot(dir.x, dir.z));
  }

  clearAmbientShips() {
    for (const s of this._ships) {
      try { s.light?.dispose(); } catch (_e) { /* noop */ }
      try { s.root.dispose(); } catch (_e) { /* noop */ }
    }
    this._ships = [];
  }

  setAmbientShipsEnabled(enabled) {
    try { this._shipRoot.setEnabled(!!enabled); } catch (_e) { /* noop */ }
  }

  _tickAmbientShips(dt, rng) {
    if (this._ships.length === 0) return;
    for (const s of this._ships) {
      s.progress += dt / s.duration;
      if (s.progress >= 1) {
        // Pick a new lane (deterministic per call though seed shifts)
        this._placeShipOnNewLane(s, rng, 4000);
        continue;
      }
      const t = s.progress;
      // Linear interpolation along the lane
      s.root.position.x = s.from.x + (s.to.x - s.from.x) * t;
      s.root.position.y = s.from.y + (s.to.y - s.from.y) * t;
      s.root.position.z = s.from.z + (s.to.z - s.from.z) * t;
    }
  }

  // ============================================================
  // Frame tick / dispose
  // ============================================================

  tick(dt, mode) {
    if (this._disposed) return;
    // Shared per-frame RNG — cheap & deterministic-ish; cumulative randomness
    // doesn't need to be reproducible across frames, just stable inside a tick.
    const rng = mkRng(Math.floor(performance.now()));
    if (mode === 'space') {
      this._tickAmbientShips(dt, rng);
    } else {
      this._tickPedestrians(dt, rng);
      this._tickWildlife(dt, rng);
    }
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.clearPedestrians();
    this.clearWildlife();
    this.clearAmbientShips();
    try { this._pedRoot.dispose(); } catch (_e) { /* noop */ }
    try { this._wildRoot.dispose(); } catch (_e) { /* noop */ }
    try { this._shipRoot.dispose(); } catch (_e) { /* noop */ }
  }
}

export default NpcSystem;
